/**
 * パルの体色(docs/14_characters.md §2「色システム(確定仕様)」)。
 *
 * 決めごと:
 *   1. 初期は**白**。設定「がんばりを いろに はんえいする」がオフのあいだも白のまま
 *   2. 学習領域のブランド色が「**直近28日の取り組み量の比率**」で混ざる
 *      (上位3領域までの重み付き平均。4番目以降は色を濁らせるだけなので混ぜない)
 *   3. 混色が灰色に濁らないよう、混ぜたあとに**彩度の下限**を掛ける
 *   4. **レインボーだけは混色で作らない**(5領域を混ぜると必ず灰色になる)。
 *      直近28日に5領域以上でそれぞれ一定量あそんだときだけ、
 *      専用のパステル多色(面を分割して塗る)に切り替える
 *   5. 取り組みが減ると白へ戻る。28日窓から古い日が抜けて合計が減る = 自然に薄くなる
 *
 * ここは**純関数だけ**(描画も localStorage も知らない)。入力は
 * `services/activityLog` の直近28日の領域べつ回数、出力は塗りの指定だけ。
 */

import {
  ACTIVITY_DOMAINS,
  totalActivity,
  type ActivityDayCounts,
  type ActivityDomain,
} from '../services/activityLog';

/** 白(初期のパル)。 */
export const PAL_WHITE = '#ffffff';

/**
 * 領域のブランド色(フラット単色。CLAUDE.md ルール7)。
 * 画面の既存パレット(`styles.css` の `--c-*`)から、領域の絵文字の印象に合うものを取る。
 */
export const DOMAIN_COLORS: Readonly<Record<ActivityDomain, string>> = {
  typing: '#4dabf7',
  puzzle: '#845ef7',
  math: '#ff922b',
  language: '#f06595',
  science: '#51cf66',
  social: '#ffd43b',
  life: '#20c997',
};

/** 混ぜる領域の数(上位いくつまで)。 */
export const MIX_DOMAINS = 3;
/** にごり防止の彩度下限(HSL の S)。 */
export const MIN_SATURATION = 0.5;
/** 体色として許す明るさの範囲(HSL の L)。暗いと「もちもち」に見えない。 */
export const MIN_LIGHTNESS = 0.5;
export const MAX_LIGHTNESS = 0.82;
/** この回数(直近28日の合計)で色がいちばん濃くなる。28日で20回 ≒ 週5回。 */
export const FULL_ACTIVITY = 20;
/** 1回でもあそんだら、うっすらは色づく(0だと「変わらない」と思われる)。 */
export const MIN_TINT = 0.25;
/** レインボーの条件: この数以上の領域で…… */
export const RAINBOW_DOMAINS = 5;
/** ……それぞれこの回数以上あそんでいること。 */
export const RAINBOW_MIN_PER_DOMAIN = 3;
/** レインボーの各色に混ぜる白の割合(パステルにする)。 */
export const RAINBOW_PASTEL = 0.45;

/**
 * 体の塗り。
 * - `solid`  … 単色(白 〜 混色)
 * - `rainbow`… 面を等分して塗り分ける多色(**グラデーションではない**)
 */
export type PalPalette =
  | { readonly kind: 'solid'; readonly color: string }
  | { readonly kind: 'rainbow'; readonly colors: readonly string[] };

export interface PalColor {
  readonly palette: PalPalette;
  /** いちばん取り組んでいる領域(色の呼び名に使う)。まだ何もしていなければ `null`。 */
  readonly leadDomain: ActivityDomain | null;
  /** 混色に効いている領域(多い順・最大 `MIX_DOMAINS`)。 */
  readonly mixedDomains: readonly ActivityDomain[];
  /** 色の濃さ(0 = まっしろ 〜 1 = いちばん濃い)。 */
  readonly tint: number;
  readonly rainbow: boolean;
}

/** まだ何もしていない(= 白い)パル。 */
export const WHITE_PAL: PalColor = {
  palette: { kind: 'solid', color: PAL_WHITE },
  leadDomain: null,
  mixedDomains: [],
  tint: 0,
  rainbow: false,
};

// ── 色の計算(内部) ─────────────────────────────────────

interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** `#rrggbb` → RGB(0〜255)。読めない文字列は白として扱う。 */
export function hexToRgb(hex: string): Rgb {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return { r: 255, g: 255, b: 255 };
  const value = Number.parseInt(match[1] ?? 'ffffff', 16);
  return { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff };
}

/** RGB → `#rrggbb`。 */
export function rgbToHex({ r, g, b }: Rgb): string {
  const part = (value: number): string =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = max === rr ? ((gg - bb) / d) % 6 : max === gg ? (bb - rr) / d + 2 : (rr - gg) / d + 4;
  return { h: (h * 60 + 360) % 360, s, l };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const [r1, g1, b1] =
    hh < 1
      ? [c, x, 0]
      : hh < 2
        ? [x, c, 0]
        : hh < 3
          ? [0, c, x]
          : hh < 4
            ? [0, x, c]
            : hh < 5
              ? [x, 0, c]
              : [c, 0, x];
  const m = l - c / 2;
  return { r: ((r1 ?? 0) + m) * 255, g: ((g1 ?? 0) + m) * 255, b: ((b1 ?? 0) + m) * 255 };
}

/** 2色を `t`(0〜1)で混ぜる。`t = 1` で `to` になる。 */
export function mixHex(from: string, to: string, t: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const k = Math.max(0, Math.min(1, t));
  return rgbToHex({
    r: a.r + (b.r - a.r) * k,
    g: a.g + (b.g - a.g) * k,
    b: a.b + (b.b - a.b) * k,
  });
}

/** 領域の色を白寄りのパステルにする(レインボー用)。 */
export function pastelOf(domain: ActivityDomain): string {
  return mixHex(DOMAIN_COLORS[domain], PAL_WHITE, RAINBOW_PASTEL);
}

/** 多い順に並べた領域(同数のときは `ACTIVITY_DOMAINS` の並び順で安定させる)。 */
function rankDomains(
  counts: ActivityDayCounts,
): readonly { domain: ActivityDomain; count: number }[] {
  return ACTIVITY_DOMAINS.map((domain) => ({ domain, count: counts[domain] ?? 0 }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count);
}

// ── 本体 ─────────────────────────────────────────────────

/**
 * 直近28日の領域べつ回数 → パルの体色。
 *
 * 混色式:
 *   1. 回数の多い上位 `MIX_DOMAINS` 領域を取り、その回数を重みに正規化する
 *   2. ブランド色を RGB の重み付き平均で混ぜる
 *   3. HSL に直して彩度を `MIN_SATURATION` 以上・明るさを規定の帯に収める(にごり防止)
 *   4. 白との線形補間で濃さ `tint` を掛ける(`tint = 0` なら白、`1` でフルの色)
 *
 * 濃さ:
 *   `tint = MIN_TINT + (1 - MIN_TINT) × min(1, 直近28日の合計 / FULL_ACTIVITY)`
 *   窓から古い日が抜けると合計が減り、白へ戻っていく(サボると白 = docs/14 §2)。
 */
export function palColorOf(counts: ActivityDayCounts): PalColor {
  const total = totalActivity(counts);
  if (total <= 0) return WHITE_PAL;

  const ranked = rankDomains(counts);
  const leadDomain = ranked[0]?.domain ?? null;

  // ── レインボー(混色では作らない特別条件)──
  const rainbowDomains = ranked
    .filter((entry) => entry.count >= RAINBOW_MIN_PER_DOMAIN)
    .map((entry) => entry.domain);
  if (rainbowDomains.length >= RAINBOW_DOMAINS) {
    // 並びは領域の定義順に戻す(あそぶたびに縞の順番が入れ替わらないように)。
    const ordered = ACTIVITY_DOMAINS.filter((domain) => rainbowDomains.includes(domain));
    return {
      palette: { kind: 'rainbow', colors: ordered.map(pastelOf) },
      leadDomain,
      mixedDomains: ordered,
      tint: 1,
      rainbow: true,
    };
  }

  // ── 混色 ──
  const top = ranked.slice(0, MIX_DOMAINS);
  const weightSum = top.reduce((sum, entry) => sum + entry.count, 0);
  let r = 0;
  let g = 0;
  let b = 0;
  for (const entry of top) {
    const weight = entry.count / weightSum;
    const rgb = hexToRgb(DOMAIN_COLORS[entry.domain]);
    r += rgb.r * weight;
    g += rgb.g * weight;
    b += rgb.b * weight;
  }
  const hsl = rgbToHsl({ r, g, b });
  const cleaned = rgbToHex(
    hslToRgb(
      hsl.h,
      Math.max(MIN_SATURATION, hsl.s),
      Math.max(MIN_LIGHTNESS, Math.min(MAX_LIGHTNESS, hsl.l)),
    ),
  );

  const tint = MIN_TINT + (1 - MIN_TINT) * Math.min(1, total / FULL_ACTIVITY);
  return {
    palette: { kind: 'solid', color: mixHex(PAL_WHITE, cleaned, tint) },
    leadDomain,
    mixedDomains: top.map((entry) => entry.domain),
    tint,
    rainbow: false,
  };
}
